import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { AlertCircle, CheckCircle2, Info, TriangleAlert, X } from 'lucide-react'
import { cn } from '../../lib/utils'

export type ToastStatus = 'success' | 'error' | 'warning' | 'info'

export type ToastOptions = {
  title: string
  description?: string
  status?: ToastStatus
  duration?: number
  isClosable?: boolean
}

type ToastItem = Required<Pick<ToastOptions, 'title' | 'status' | 'isClosable'>> &
  Pick<ToastOptions, 'description'> & { id: number }

type ToastHandler = (options: ToastOptions) => number

const ToastContext = createContext<ToastHandler | null>(null)

const toastStyles: Record<ToastStatus, { icon: typeof Info; className: string }> = {
  success: { icon: CheckCircle2, className: 'border-emerald-200 bg-emerald-50 text-emerald-900' },
  error: { icon: AlertCircle, className: 'border-rose-200 bg-rose-50 text-rose-900' },
  warning: { icon: TriangleAlert, className: 'border-amber-200 bg-amber-50 text-amber-900' },
  info: { icon: Info, className: 'border-blue-200 bg-blue-50 text-blue-900' }
}

export const ToastProvider = ({ children }: { children: React.ReactNode }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const nextId = useRef(1)
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>())

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id)
    if (timer) clearTimeout(timer)
    timers.current.delete(id)
    setToasts((current) => current.filter((toast) => toast.id !== id))
  }, [])

  const showToast = useCallback<ToastHandler>((options) => {
    const id = nextId.current++
    const duration = options.duration ?? 3500
    setToasts((current) => [
      ...current.slice(-3),
      {
        id,
        title: options.title,
        description: options.description,
        status: options.status ?? 'info',
        isClosable: options.isClosable ?? true
      }
    ])
    if (duration > 0) {
      timers.current.set(id, setTimeout(() => dismiss(id), duration))
    }
    return id
  }, [dismiss])

  useEffect(() => () => {
    timers.current.forEach(clearTimeout)
    timers.current.clear()
  }, [])

  const value = useMemo(() => showToast, [showToast])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        aria-relevant="additions"
        className="pointer-events-none fixed right-4 top-4 z-[100] flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2"
      >
        {toasts.map((toast) => {
          const style = toastStyles[toast.status]
          const Icon = style.icon
          return (
            <div
              key={toast.id}
              role={toast.status === 'error' ? 'alert' : 'status'}
              className={cn('pointer-events-auto flex items-start gap-3 rounded-lg border p-4 shadow-lg', style.className)}
            >
              <Icon className="mt-0.5 h-5 w-5 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold leading-5">{toast.title}</p>
                {toast.description ? <p className="mt-1 text-sm leading-5 opacity-80">{toast.description}</p> : null}
              </div>
              {toast.isClosable ? (
                <button
                  type="button"
                  aria-label="알림 닫기"
                  className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md opacity-60 hover:bg-black/5 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current"
                  onClick={() => dismiss(toast.id)}
                >
                  <X className="h-4 w-4" />
                </button>
              ) : null}
            </div>
          )
        })}
      </div>
    </ToastContext.Provider>
  )
}

export const useToast = () => {
  const toast = useContext(ToastContext)
  if (!toast) throw new Error('useToast must be used inside ToastProvider')
  return toast
}
